import { useContext } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import { AuthContext, AuthProvider } from "./components/Auth/AuthContext";
import DemoLimitDialog from "./demo/DemoLimitDialog";
import DemoTour from "./demo/DemoTour";
import EmailActionHandler from "./components/Auth/EmailActionHandler";
import ForgotPassword from "./components/Auth/ForgotPassword";
import Login from "./components/Auth/Login";
import Logout from "./components/Auth/Logout";
import Register from "./components/Auth/Register";
import ResetPassword from "./components/Auth/ResetPassword";
import AddCategory from "./components/Categories/AddCategory";
import Categories from "./components/Categories/Categories";
import EditCategory from "./components/Categories/EditCategory";
import GroupDetailsPage from "./components/Groups/GroupDetailsPage";
import GroupsPage from "./components/Groups/GroupsPage";
import { GroupContextProvider } from "./components/Groups/useGroupContext";
import ProfilePage from "./components/Profile/ProfilePage";
import ProtectedRoute from "./components/ProtectedRoute";
import StatsPage from "./components/Stats/StatsPage";
import AddHoldingPage from "./components/Stocks/AddHoldingPage";
import BondsPage from "./components/Stocks/Bonds/BondsPage";
import CashFlowsPage from "./components/Stocks/Bonds/CashFlowsPage";
import BrokersPage from "./components/Stocks/BrokersPage";
import HoldingsPage from "./components/Stocks/HoldingsPage";
import PortfolioExportPage from "./components/Stocks/PortfolioExportPage";
import PortfolioImportPage from "./components/Stocks/PortfolioImportPage";
import PortfolioOverviewPage from "./components/Stocks/PortfolioOverviewPage";
import PositionsPage from "./components/Stocks/PositionsPage";
import StockDetailPage from "./components/Stocks/StockDetailPage";
import AddTransaction from "./components/Transactions/AddTransaction";
import EditExpense from "./components/Transactions/EditExpense";
import EditIncome from "./components/Transactions/EditIncome";
import EditInvestment from "./components/Transactions/EditInvestment";
import EditRecurringPayment from "./components/Transactions/EditRecurringPayment";
import EditTransaction from "./components/Transactions/EditTransaction";
import Expenses from "./components/Transactions/Expenses";
import TransactionExportPage from "./components/Transactions/import/TransactionExportPage";
import TransactionImportPage from "./components/Transactions/import/TransactionImportPage";
import Income from "./components/Transactions/Income";
import Investments from "./components/Transactions/Investments";
import RecurringPayment from "./components/Transactions/RecurringPayment";
import Navbar from "./Navbar";

const RootRoute = () => {
  const { isLoggedIn, loading } = useContext(AuthContext);

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <div>Loading...</div>
      </div>
    );
  }

  if (!isLoggedIn) {
    return <Navigate to="/login" replace />;
  }
  return <Navigate to="/dashboard" replace />;
};

function AppContent() {
  const { demoLimitType, clearDemoLimit } = useContext(AuthContext);

  return (
    <>
      <DemoTour />
      <DemoLimitDialog
        open={!!demoLimitType}
        limitType={demoLimitType}
        onClose={clearDemoLimit}
      />
      <Navbar />
      <Routes>
        <Route path="/" element={<RootRoute />} />

        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/auth/action" element={<EmailActionHandler />} />
        <Route path="/logout" element={<Logout />} />

        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <StatsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/expenses"
          element={
            <ProtectedRoute>
              <Expenses />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-transaction"
          element={
            <ProtectedRoute>
              <AddTransaction />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-transaction/:id"
          element={
            <ProtectedRoute>
              <EditTransaction />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-expense/:id"
          element={
            <ProtectedRoute>
              <EditExpense />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-income/:id"
          element={
            <ProtectedRoute>
              <EditIncome />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-investment/:id"
          element={
            <ProtectedRoute>
              <EditInvestment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/income"
          element={
            <ProtectedRoute>
              <Income />
            </ProtectedRoute>
          }
        />
        <Route
          path="/investments"
          element={
            <ProtectedRoute>
              <Investments />
            </ProtectedRoute>
          }
        />
        <Route
          path="/recurring-payments"
          element={
            <ProtectedRoute>
              <RecurringPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-payment/:id"
          element={
            <ProtectedRoute>
              <EditRecurringPayment />
            </ProtectedRoute>
          }
        />
        <Route
          path="/categories"
          element={
            <ProtectedRoute>
              <Categories />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-category"
          element={
            <ProtectedRoute>
              <AddCategory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-category/:id"
          element={
            <ProtectedRoute>
              <EditCategory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import-transactions"
          element={
            <ProtectedRoute>
              <TransactionImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export-transactions"
          element={
            <ProtectedRoute>
              <TransactionExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <ProfilePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups"
          element={
            <ProtectedRoute>
              <GroupsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/groups/:id"
          element={
            <ProtectedRoute>
              <GroupDetailsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/portfolio"
          element={
            <ProtectedRoute>
              <PortfolioOverviewPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/brokers"
          element={
            <ProtectedRoute>
              <BrokersPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stock-transactions"
          element={
            <ProtectedRoute>
              <HoldingsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-stock-transaction"
          element={
            <ProtectedRoute>
              <AddHoldingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/edit-stock-transaction/:id"
          element={
            <ProtectedRoute>
              <AddHoldingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/import-portfolio"
          element={
            <ProtectedRoute>
              <PortfolioImportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/export-portfolio"
          element={
            <ProtectedRoute>
              <PortfolioExportPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/positions"
          element={
            <ProtectedRoute>
              <PositionsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/stocks/:ticker"
          element={
            <ProtectedRoute>
              <StockDetailPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/bonds"
          element={
            <ProtectedRoute>
              <BondsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/cash-flows"
          element={
            <ProtectedRoute>
              <CashFlowsPage />
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <GroupContextProvider>
        <Router>
          <AppContent />
        </Router>
      </GroupContextProvider>
    </AuthProvider>
  );
}

export default App;
